import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Clock, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { AvailabilitySlot } from "@/domain/configurator";
import type { MeetingData } from "./index";
import { cn } from "@/lib/utils";

interface MeetingScheduleStepProps {
  data: MeetingData;
  onChange: (data: MeetingData) => void;
  slots: AvailabilitySlot[];
  isLoadingSlots: boolean;
  onSchedule: () => void;
  isSubmitting: boolean;
  onBack: () => void;
}

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MEETING_DURATIONS = [
  { duration: 30, label: "30 minutes", description: "Quick overview" },
  { duration: 45, label: "45 minutes", description: "Detailed discussion" },
  {
    duration: 60,
    label: "60 minutes",
    description: "Comprehensive consultation",
  },
] as const;

export function MeetingScheduleStep({
  data,
  onChange,
  slots,
  isLoadingSlots,
  onSchedule,
  isSubmitting,
  onBack,
}: MeetingScheduleStepProps) {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  const maxDate = useMemo(() => {
    const max = new Date();
    max.setDate(max.getDate() + 30);
    return max;
  }, []);

  const availableDates = useMemo(() => {
    return new Set(
      slots.map((s) => {
        const date = new Date(s.date);
        return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
      }),
    );
  }, [slots]);

  const slotsForDate = useMemo(() => {
    if (!selectedDate) return [];
    return slots.filter((s) => {
      const date = new Date(s.date);
      return (
        `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}` ===
        selectedDate
      );
    });
  }, [slots, selectedDate]);

  const daysInMonth = useMemo(() => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysArray = [];

    for (let i = 0; i < firstDay.getDay(); i++) {
      daysArray.push(null);
    }

    for (let i = 1; i <= lastDay.getDate(); i++) {
      daysArray.push(i);
    }

    return daysArray;
  }, [currentMonth, currentYear]);

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDateClick = (day: number) => {
    const dateKey = `${currentYear}-${currentMonth}-${day}`;
    setSelectedDate(dateKey);
    onChange({ ...data, selectedSlot: null });
  };

  const handleSlotSelect = (slot: AvailabilitySlot) => {
    onChange({ ...data, selectedSlot: slot });
  };

  const isDateAvailable = (day: number) => {
    const dateKey = `${currentYear}-${currentMonth}-${day}`;
    return availableDates.has(dateKey);
  };

  const isDateInPast = (day: number) => {
    const date = new Date(currentYear, currentMonth, day);
    return date < today;
  };

  const isDateInFuture = (day: number) => {
    const date = new Date(currentYear, currentMonth, day);
    return date > maxDate;
  };

  const isValid = data.selectedSlot !== null;

  return (
    <Card className="glass-card border-gradient">
      <CardContent className="p-6 md:p-8 space-y-6">
        <div>
          <h2 className="font-display text-2xl font-bold mb-2">
            Schedule Discovery Meeting
          </h2>
          <p className="text-muted-foreground">
            Choose a convenient time for your discovery consultation.
          </p>
        </div>

        <div>
          <Label className="text-base font-semibold mb-3 block">
            Meeting Duration
          </Label>
          <div className="grid grid-cols-3 gap-3">
            {MEETING_DURATIONS.map((option) => (
              <button
                key={option.duration}
                onClick={() =>
                  onChange({ ...data, meetingDuration: option.duration })
                }
                className={cn(
                  "p-4 rounded-lg border-2 transition-all text-center",
                  data.meetingDuration === option.duration
                    ? "border-primary bg-primary/10"
                    : "border-border hover:border-primary/50",
                )}
              >
                <Clock className="h-5 w-5 mx-auto mb-2" />
                <p className="font-semibold text-sm">{option.label}</p>
                <p className="text-xs text-muted-foreground">
                  {option.description}
                </p>
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-base font-semibold mb-3 block">
            Select Date & Time
          </Label>

          {isLoadingSlots ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-card/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={handlePrevMonth}
                    className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <h3 className="font-semibold">
                    {MONTHS[currentMonth]} {currentYear}
                  </h3>
                  <button
                    onClick={handleNextMonth}
                    className="p-2 hover:bg-primary/10 rounded-lg transition-colors"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>

                <div className="grid grid-cols-7 gap-2">
                  {DAYS.map((day) => (
                    <div
                      key={day}
                      className="text-center text-xs font-medium text-muted-foreground py-2"
                    >
                      {day}
                    </div>
                  ))}
                  {daysInMonth.map((day, index) => {
                    if (day === null) {
                      return <div key={`empty-${index}`} />;
                    }

                    const available = isDateAvailable(day);
                    const isPast = isDateInPast(day);
                    const isFuture = isDateInFuture(day);
                    const isSelected =
                      selectedDate === `${currentYear}-${currentMonth}-${day}`;

                    return (
                      <button
                        key={day}
                        onClick={() =>
                          !isPast && !isFuture && handleDateClick(day)
                        }
                        disabled={isPast || isFuture || !available}
                        className={cn(
                          "aspect-square rounded-lg text-sm font-medium transition-all",
                          isSelected
                            ? "bg-primary text-primary-foreground"
                            : available && !isPast && !isFuture
                              ? "bg-primary/10 hover:bg-primary/20 text-primary"
                              : "text-muted-foreground/50 cursor-not-allowed",
                        )}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>

              <AnimatePresence mode="wait">
                {selectedDate && slotsForDate.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className="space-y-2"
                  >
                    <Label className="text-sm font-medium">
                      Available Time Slots
                    </Label>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                      {slotsForDate.map((slot) => (
                        <button
                          key={slot.id}
                          onClick={() => handleSlotSelect(slot)}
                          className={cn(
                            "p-3 rounded-lg border-2 text-sm font-medium transition-all",
                            data.selectedSlot?.id === slot.id
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-border hover:border-primary/50",
                          )}
                        >
                          {slot.availabilityWindow}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          )}
        </div>

        <div>
          <Label htmlFor="discussionTopics" className="text-base font-semibold">
            Discussion Topics (Optional)
          </Label>
          <p className="text-sm text-muted-foreground mb-2">
            Any specific topics you'd like to discuss during the meeting?
          </p>
          <Textarea
            id="discussionTopics"
            value={data.discussionTopics}
            onChange={(e) =>
              onChange({ ...data, discussionTopics: e.target.value })
            }
            placeholder="E.g., Technical architecture, timeline, team structure..."
            rows={3}
            className="resize-none"
          />
        </div>

        <div className="flex justify-between pt-4">
          <Button onClick={onBack} variant="outline" size="lg">
            Back
          </Button>
          <Button
            onClick={onSchedule}
            disabled={!isValid || isSubmitting}
            size="lg"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Scheduling...
              </>
            ) : (
              "Schedule Meeting"
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
